import React, { useState } from 'react'
import ReactDOM from 'react-dom';
import styles from './styles.css'
import {
  defineMessages,
  FormattedMessage
} from 'react-intl'
import Modal from 'components/Modal'
import Button from 'components/Button'
import { SEND_MESSAGE_MODAL } from 'constants/modals'
import cn from 'classnames'
import { sendGAClickEvent } from 'utils/googleAnalytics'
import LoadingSpinner from 'components/LoadingSpinner'
import Bowser from 'bowser'
import FormattedCurrency from 'components/FormattedCurrency'
import { isIOS } from 'utils/user'

const NOT_ENOUGH_MONEY = 'notEnoughMoneyError'
const NOT_AGREE = 'userDoesNotAgreeTermsError'
const EMPTY_MESSAGE = 'messageIsEmptyError'

const messages = defineMessages({
  close: 'Close',
  sendMessage: 'Send Message',
  perMessage: 'per message',
  title: 'Type a Message',
  confirmationTitle: 'Confirmation',
  confirmationBody: 'The message has been sent',
  freeMessagesAvailable: 'Use your free message credit',
  agree: 'I agree to the',
  terms: 'Terms & Conditions',
  reviewInfo: 'Message is subject to review by the facility',
  [NOT_ENOUGH_MONEY]: 'You do not have enough money',
  [NOT_AGREE]: 'You need to agree to the Terms and Conditions',
  [EMPTY_MESSAGE]: 'Message should not be empty',
  'Error message - #create: Validation failed: Body exceeds character limit': 'Length of the message exceeds character limit',
  'Error message - #create: Validation failed: Not enough balance to send message': 'Not enough balance to send message'
})

function SendMessageModal(props) {
  const {
    show,
    closeModal,
    redirectURL,
    balance,
    rate,
    maxLength,
    freeMessages,
    sendTextMessage,
    serverError,
    loading
  } = props

  const [message, setMessage] = useState('')
  const [agree, setAgree] = useState(false)
  const [error, setError] = useState(null)
  const [messageSent, setMessageSent] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [maxMsgLength, setMaxMsgLength] = useState(maxLength)

  const browser = Bowser.getParser(window.navigator.userAgent)
  const isSafari = browser.getBrowserName().toLowerCase() === 'safari'

  const handleAgreeChange = (e) => {
    const val = e.target.checked
    if (val && error === NOT_AGREE) {
      setError(null)
    }
    setAgree(val)
  }

  const handleMessageChange = (e) => {
    const val = e.target.value

    if (val.length > maxLength) return

    if (val.replace(/\s/g, '') && error === EMPTY_MESSAGE) {
      setError(null)
    }
    // Fixing a case in Safari when counter on UI shows 499/500 chars but user cannot input 500th character.
    // For some reason in Safari browser textarea element treats new line char as 2 characters when 'maxlength' validation happens
    // whereas actual data sent to the server contains one char for each new line. So here we add 1 to 'maxlength' attribute value
    // for each new line character to make UI consistent with what we send to the server.
    if (isSafari) {
      const newLineCharsNumber = val.split(/\r\n|\r|\n/).length - 1
      setMaxMsgLength(maxLength + newLineCharsNumber)
    }
    setMessage(val)
  }

  // Handling pasting process manually to fix iOS related bug https://jira.gtl.net/browse/VIDEO-3317
  const handlePaste = (e) => {
    if (!isIOS()) return
    const textarea = e.target
    const remainingChars = maxLength - message.length
    const paste = (e.clipboardData || window.clipboardData).getData('text').substr(0, remainingChars)
    setMessage(message.substring(0, textarea.selectionStart) + paste + message.substring(textarea.selectionStart, message.length))
    e.preventDefault()
  }

  const isValid = () => {
    if (balance < rate && freeMessages <= 0) {
      setError(NOT_ENOUGH_MONEY)
      return false
    } else if (!message.replace(/\s/g, '')) {
      setError(EMPTY_MESSAGE)
      return false
    } else if (!agree) {
      setError(NOT_AGREE)
      return false
    } else {
      return true
    }
  }

  const handleSend = () => {
    if (!loading && isValid()) {
      sendTextMessage(message, () => {
        sendGAClickEvent('CTA_Voicemail_Msg_Sent')
        setMessageSent(true)
      })
    }
  }

  const openTermsAndConditions = (e) => {
    if (window.navigator.userAgent.includes('Android')) {
      e.preventDefault()
      setShowTerms(true)
    }
  }

  const androidTermsAndConditions = ReactDOM.createPortal(
    <div className={styles.termsAndConditions}>
      <iframe src="https://www.gettingout.com/telmate-terms-of-use" title="Terms And Conditions" />
      <Button type='ok' onClick={() => {setShowTerms(false)}}>
        <FormattedMessage {...messages.close} />
      </Button>
    </div>,
    document.body
  )

  return (
    <Modal
      title={(<FormattedMessage {...(messageSent ? messages.confirmationTitle : messages.title)} />)}
      additionalHeaderItems={(
        <>
          { !messageSent && (
            <>
              <div className={styles.balance}>
                <FormattedCurrency value={balance} />
              </div>
              <div className={styles.costPerMinute}>
                { freeMessages <= 0 && (
                  <>
                    <FormattedCurrency value={rate} />&nbsp;
                    <FormattedMessage {...messages.perMessage} />
                  </>
                )}
                { freeMessages > 0 && (
                  <FormattedMessage {...messages.freeMessagesAvailable} />
                )}
              </div>
            </>
          )}
        </>
      )}
      show={show}
      footer={(
        <>
          { !messageSent && (
            <Button
              type='go'
              onClick={handleSend}
            >
              {
                loading
                  ? <LoadingSpinner color='white' size='25px' />
                  : <FormattedMessage {...messages.sendMessage} />
              }
            </Button>
          )}
          <Button
            type='ok'
            onClick={() => {
              sendGAClickEvent('CTA_Close_Visit_Modal')
              closeModal(SEND_MESSAGE_MODAL)
              if (redirectURL) {
                window.location.replace(redirectURL)
              }
            }}
          >
            <FormattedMessage {...messages.close} />
          </Button>
          { !messageSent && (
            <div className={styles.reviewText}><FormattedMessage {...messages.reviewInfo} /></div>
          )}
        </>
      )}
    >
      <>
        { !messageSent && (
          <div styleName='body'>
            { error && (
              <div styleName='error'><FormattedMessage {...messages[error]} /></div>
            )}
            { serverError && (
              <div styleName='error'>
                { messages[serverError.errorDetails]
                    ? <FormattedMessage {...messages[error]} />
                    : <span>{serverError.errorDetails}</span>
                }
              </div>
            )}
            <div styleName='counter'>{message.length}/{maxLength}</div>
            <textarea
              styleName={cn('messageTextInput', { error: error === EMPTY_MESSAGE })}
              maxLength={maxMsgLength}
              value={message}
              onChange={handleMessageChange}
              onPaste={handlePaste}
            />
            <div>
              <input type='checkbox' id='agree' styleName='agreeCheckbox' checked={agree} onChange={handleAgreeChange} />
              <label htmlFor='agree' styleName='agreeLabel'>
                <FormattedMessage {...messages.agree}/>&nbsp;
                <a onClick={openTermsAndConditions} target='_blank' href='https://www.gettingout.com/telmate-terms-of-use'>
                  <FormattedMessage {...messages.terms}/>
                </a>
              </label>
            </div>
          </div>
        )}

        { messageSent && (
          <div styleName='body'>
            <FormattedMessage {...messages.confirmationBody} />
          </div>
        )}

        {showTerms && androidTermsAndConditions}
      </>
    </Modal>
  )
}

export default SendMessageModal
